import { ulid } from "ulid";
import type { ExcelSkillsGateSnapshot } from "../../shared/featureFlags/pickerSkills";
import type { SkillSubmissionSnapshot } from "../../shared/skills/runtime/skillTurnBootstrap";
import type { ExcelSubmittedResearchSelection } from "../researchSelection";
import type { ExcelOperationEvidence } from "./operationEvidence";
import type { UserSkillRunContext } from "./runContext";

/**
 * In-memory registry of Excel Skill operations.
 *
 * One request, the clarification replies that answer it, and any paused-plan
 * resumes are a single operation under a single frozen policy. This module is
 * where that freeze lives.
 *
 * Module-level and never serialized. The paused-plan payload is written to
 * `sessionStorage`, so the context itself must live only here; the payload
 * carries an opaque id and a boolean, neither of which reveals guidance.
 *
 * A continuation reuses the frozen record from the original submission rather
 * than re-deriving it, so a catalog edit, a research-selection change, or a
 * rollout flip mid-operation cannot silently change what a half-finished run is
 * operating under.
 *
 * Lifecycle is owned by the turn that ends the operation, never by the act of
 * consuming a continuation affordance: clicking "Continue remaining work"
 * starts the very run that needs the binding.
 */

/**
 * Who an operation belongs to. Every continuation must prove it is still the
 * same account, workbook, chat session, and tab before any model or tool work
 * starts.
 */
export interface ExcelOperationOwner {
  /** Tenant-aware account scope. `null` only before authentication resolves. */
  readonly accountScopeKey: string | null;
  /** Canonical workbook key the run was pinned to. `""` is the no-workbook slot. */
  readonly workbookKey: string;
  readonly sessionId: string;
  readonly tabId: string;
}

/** Everything frozen at submit, before any resolution or model work. */
export interface ExcelOperationFreeze {
  readonly operationId: string;
  readonly owner: ExcelOperationOwner;
  /** The request as submitted. Later replies are routing hints, not requests. */
  readonly originalQuery: string;
  /** What the Skill picker reported at submit, including an explicit clear. */
  readonly skillSelection: SkillSubmissionSnapshot;
  /** Rollout gate as read at submit, so a mid-operation flip fails closed. */
  readonly gateSnapshot: ExcelSkillsGateSnapshot;
  /** Research allowlist as submitted. Skills may narrow it, never widen it. */
  readonly researchSelection: ExcelSubmittedResearchSelection;
}

export interface ExcelOperationRecord extends ExcelOperationFreeze {
  /**
   * The resolved Skill policy, sealed once the first turn settles. `undefined`
   * after sealing means the operation is sealed as "no Skill", which a
   * continuation must inherit rather than re-resolve.
   */
  readonly skillContext?: UserSkillRunContext;
  readonly policySealed: boolean;
  /**
   * Research this operation already completed. Lives here rather than in the
   * checkpoint because it holds answers and citation payloads, and the
   * checkpoint is persisted.
   */
  readonly evidence?: ExcelOperationEvidence;
}

/**
 * What the operation can still do.
 *
 * `active` is a run in flight. `resumable` is a settled run that left something
 * the user can click — a "Continue remaining work" action or an unanswered
 * clarification. There is no third state: an operation nothing can resume is
 * deleted by `releaseSkillContinuation` rather than kept in a dead state.
 */
type ExcelOperationLifecycle = "active" | "resumable";

interface ExcelOperationEntry {
  record: ExcelOperationRecord;
  lifecycle: ExcelOperationLifecycle;
  /** Registry generation this entry was opened in. */
  generation: number;
}

const operations = new Map<string, ExcelOperationEntry>();
/**
 * Bumped whenever the registry is cleared for an account change or a pane
 * teardown. Work that started under the previous generation carries its number
 * and is refused rather than allowed to re-populate a registry that was
 * deliberately emptied.
 */
let registryGeneration = 0;
/**
 * Memory backstop, not a working-set limit.
 *
 * Every entry here is either running or resumable, so there is nothing in the
 * registry that is safe to evict: each one is work the user can still see a
 * button for. The ceiling is set far above any plausible session, so reaching
 * it means operations are being opened and never settled — a leak somewhere
 * else, not ordinary use.
 */
export const MAX_SKILL_OPERATIONS = 200;

/**
 * Freezes a record and the snapshots it hands out.
 *
 * `Object.freeze` is shallow, and every turn of the operation reads this
 * record. Freezing the owner and the research allowlist too is what makes "the
 * scope this operation was submitted under" a fact rather than a convention: a
 * later turn cannot push a source onto the array and widen work already in
 * progress. The research arrays are copied first because the caller still uses
 * its own selection for the turn that opened the operation, and freezing a
 * live object it owns would be a surprise rather than a guarantee.
 *
 * `skillSelection` arrives already deeply frozen from
 * `snapshotSkillSubmissionOptions`, and `skillContext` from the resolver.
 */
function freezeRecord(record: ExcelOperationRecord): ExcelOperationRecord {
  return Object.freeze({
    ...record,
    owner: Object.freeze({ ...record.owner }),
    gateSnapshot: Object.freeze({ ...record.gateSnapshot }),
    researchSelection: Object.freeze({
      ...record.researchSelection,
      dataSources: frozenCopy(record.researchSelection.dataSources),
      userFileIds: frozenCopy(record.researchSelection.userFileIds),
    }),
  });
}

/**
 * A frozen copy that still satisfies the mutable array type the shared
 * research-selection shape declares. That declared mutability is exactly what
 * this is guarding against, so the cast is the narrower statement of the two.
 */
function frozenCopy<T>(values: readonly T[]): T[] {
  return Object.freeze([...values]) as T[];
}

/**
 * The registry's current generation, captured by a submission before its first
 * await and handed back to `openSkillOperation`.
 */
export function getSkillOperationsGeneration(): number {
  return registryGeneration;
}

export function createSkillContinuationId(): string {
  return `xlskill_${ulid()}`;
}

/**
 * Whether a submission's Skill selection is the one an operation froze.
 *
 * Ordered by id, because order is what the resolver treats as guidance
 * precedence. A reply that re-sends the same selection is still the same
 * operation; a different one starts a new operation instead of mixing two
 * policies into a single request.
 */
export function sameSkillSelection(
  frozen: SkillSubmissionSnapshot | undefined,
  submitted: SkillSubmissionSnapshot
): boolean {
  if (!frozen) return false;
  return (
    frozen.selectedSkills.length === submitted.selectedSkills.length &&
    frozen.selectedSkills.every((skill, index) => skill.id === submitted.selectedSkills[index]?.id)
  );
}

/**
 * Freezes a new operation's context at submit time.
 *
 * Called before resolution so the record exists for the whole operation, not
 * just for the part of it that turned out to use a Skill.
 *
 * Returns whether the operation was opened. A submission that captured a
 * generation the registry has since left belongs to an account that is no
 * longer signed in, and re-populating the registry on its behalf is exactly
 * what the clear was meant to prevent. A caller that is refused must not start
 * the run: an unfrozen run is the silent policy loss this module exists to
 * prevent.
 */
export function openSkillOperation(
  freeze: ExcelOperationFreeze,
  options: { readonly expectedGeneration?: number } = {},
): boolean {
  if (
    options.expectedGeneration !== undefined &&
    options.expectedGeneration !== registryGeneration
  ) {
    return false;
  }
  if (!operations.has(freeze.operationId) && operations.size >= MAX_SKILL_OPERATIONS) {
    // Refusing the new submission is recoverable — the user resends — while
    // making room by dropping an existing entry destroys a paused plan or an
    // unanswered clarification the user can still click, and does it silently.
    console.warn(
      `[excel-skills] Operation registry is at its ${MAX_SKILL_OPERATIONS}-entry ceiling; refusing a new operation.`
    );
    return false;
  }
  operations.set(freeze.operationId, {
    record: freezeRecord({ ...freeze, policySealed: false }),
    lifecycle: "active",
    generation: registryGeneration,
  });
  return true;
}

/**
 * Seals the resolved policy onto the operation.
 *
 * A `undefined` context is a real outcome, not a missing one: it seals the
 * operation as "no Skill" so a later reply inherits that instead of inferring a
 * Skill the user never chose.
 *
 * Sealing is one-way. Every turn of an operation calls this, and a continuation
 * passes back the context it inherited, so re-sealing is normal; what must not
 * happen is a late turn — or a run whose account is gone — replacing a policy
 * the operation is already running under.
 */
export function sealSkillOperationPolicy(
  operationId: string,
  context: UserSkillRunContext | undefined,
): boolean {
  const entry = operations.get(operationId);
  if (!entry || entry.generation !== registryGeneration || entry.record.policySealed) return false;
  entry.record = freezeRecord({
    ...entry.record,
    ...(context ? { skillContext: context } : {}),
    policySealed: true,
  });
  return true;
}

/**
 * Follows the operation when its run is relocated to another workbook.
 *
 * A cross-workbook handoff moves the run and its chat tab to the target
 * workbook's slot, and any resume affordance it later publishes carries the
 * target key. Without this the operation would still be owned by the workbook
 * it was submitted from, and its own resume would be refused as a workbook
 * change.
 */
export function rebindSkillOperationWorkbook(operationId: string, workbookKey: string): boolean {
  const entry = liveEntry(operationId);
  if (!entry) return false;
  entry.record = freezeRecord({
    ...entry.record,
    owner: { ...entry.record.owner, workbookKey },
  });
  return true;
}

/**
 * Follows every operation a workbook owns when Save As replaces its identity.
 *
 * A rename changes the key, not the conversation: the same chat, the same tab,
 * the same half-finished request. Chat history and its checkpoint actions are
 * retargeted to the new key, so the operations behind them have to move with
 * them — otherwise the resume the user can still see is refused as a workbook
 * change, from the only workbook it could ever be clicked in.
 *
 * Unlike the per-run rebind above this is keyed by workbook rather than by
 * operation, because a rename applies to everything the file holds, including
 * operations belonging to chat tabs no run is currently touching.
 *
 * Returns how many operations moved.
 */
export function retargetSkillOperationsWorkbook(
  sourceWorkbookKey: string,
  targetWorkbookKey: string,
): number {
  if (sourceWorkbookKey === targetWorkbookKey) return 0;
  let moved = 0;
  for (const entry of operations.values()) {
    if (entry.generation !== registryGeneration) continue;
    if (entry.record.owner.workbookKey !== sourceWorkbookKey) continue;
    entry.record = freezeRecord({
      ...entry.record,
      owner: { ...entry.record.owner, workbookKey: targetWorkbookKey },
    });
    moved += 1;
  }
  return moved;
}

/**
 * Records whether the operation can still be resumed.
 *
 * Called once per turn, from the outcome that publishes (or withholds) the
 * resume affordance, so retention and what the user can actually click cannot
 * drift apart. Returns whether the operation is still there to retain: a
 * `false` means the affordance the caller is about to publish would refuse on
 * click, because the account it belonged to is gone.
 */
export function markSkillContinuationRetained(
  operationId: string | undefined,
  retained: boolean,
): boolean {
  const entry = liveEntry(operationId);
  if (!entry) return false;
  entry.lifecycle = retained ? "resumable" : "active";
  return true;
}

/**
 * Stores the operation's research evidence.
 *
 * Refuses a pack belonging to another operation, so a stale callback from an
 * abandoned run cannot write its findings onto whatever operation replaced it.
 */
export function recordSkillOperationEvidence(
  operationId: string | undefined,
  evidence: ExcelOperationEvidence,
): boolean {
  if (!operationId || evidence.operationId !== operationId) return false;
  const entry = liveEntry(operationId);
  if (!entry) return false;
  entry.record = freezeRecord({ ...entry.record, evidence });
  return true;
}

/**
 * The entry a mutation is allowed to write to.
 *
 * Anything opened before the last clear belongs to an account that is no longer
 * signed in. Clearing already removes those entries, so this is the backstop
 * for the ordering the caller cannot see: a callback that was queued before the
 * switch and runs after it.
 */
function liveEntry(operationId: string | undefined): ExcelOperationEntry | undefined {
  if (!operationId) return undefined;
  const entry = operations.get(operationId);
  if (!entry || entry.generation !== registryGeneration) return undefined;
  return entry;
}

export function readSkillOperation(
  operationId: string | undefined,
): ExcelOperationRecord | undefined {
  if (!operationId) return undefined;
  return operations.get(operationId)?.record;
}

export function releaseSkillContinuation(operationId: string | undefined): void {
  if (!operationId) return;
  operations.delete(operationId);
}

/**
 * Drops every operation, for an account change or logout.
 *
 * Guidance and sealed policy belong to the account that resolved them, so they
 * must not survive into a different one even for the same user.
 */
export function clearSkillOperations(): void {
  operations.clear();
  registryGeneration += 1;
}

export function resetSkillContinuationsForTests(): void {
  operations.clear();
  registryGeneration += 1;
}
